import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empMedicalService } from '@/services/empMedicalService'
import { employeesService } from '@/services/employeesService'
import type { MedicalVm } from '@/api/types/empMedical.types'
import { showError, showSuccess } from '@/lib/swal'

function toDateInputValue(d?: string): string {
  if (!d) return ''
  try {
    const date = new Date(d)
    return date.toISOString().slice(0, 10)
  } catch {
    return String(d).slice(0, 10)
  }
}

export const MedicalEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const tm = (key: string) => t(`empMedical.${key}`)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [medicalCompanies, setMedicalCompanies] = useState<{ id?: number; name?: string; lookupNameEn?: string; lookupNameAr?: string }[]>([])
  const [medicalClasses, setMedicalClasses] = useState<{ id?: number; name?: string; lookupNameEn?: string; lookupNameAr?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    medicalClassId: 0,
    medicalCard: '',
    issueDate: '',
    expiryDate: '',
    bloodGroup: '',
    medicalCompanyId: 0,
    medicalDetail: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    empMedicalService.getMedicalCompanies().then((data) => setMedicalCompanies(data ?? [])).catch(() => setMedicalCompanies([]))
    empMedicalService.getMedicalClasses().then((data) => setMedicalClasses(data ?? [])).catch(() => setMedicalClasses([]))
  }, [])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empMedicalService.getById(Number(id))
        const raw = data as any
        setForm({
          empId: raw.empId ?? raw.employeeId ?? 0,
          medicalClassId: raw.medicalClassId ?? 0,
          medicalCard: raw.medicalCard ?? '',
          issueDate: toDateInputValue(raw.issueDate),
          expiryDate: toDateInputValue(raw.expiryDate),
          bloodGroup: raw.bloodGroup ?? '',
          medicalCompanyId: raw.medicalCompanyId ?? 0,
          medicalDetail: raw.medicalDetail ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? tm('loadFailed'))
        navigate('/medicals')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label =
          fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? t('empMedical.employeeFallback', { id: e.id ?? 0 })
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees, t]
  )

  const medicalCompanyOptions = useMemo(
    () =>
      medicalCompanies.map((mc) => {
        const name =
          (mc as any).lookupNameEn ??
          (mc as any).lookupNameAr ??
          (mc as any).name ??
          t('empMedical.companyFallback', { id: mc.id ?? 0 })
        return { value: String(mc.id ?? 0), label: name }
      }),
    [medicalCompanies, t]
  )

  const medicalClassOptions = useMemo(
    () =>
      medicalClasses.map((cl) => {
        const name =
          (cl as any).lookupNameEn ??
          (cl as any).lookupNameAr ??
          (cl as any).name ??
          t('empMedical.classFallback', { id: cl.id ?? 0 })
        return { value: String(cl.id ?? 0), label: name }
      }),
    [medicalClasses, t]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(tm('branchRequired'))
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error(tm('employeeRequired'))
      return
    }
    if (!form.medicalClassId || form.medicalClassId === 0) {
      toast.error(tm('insuranceClassRequired'))
      return
    }
    if (!form.medicalCard.trim()) {
      toast.error(tm('medicalCardRequired'))
      return
    }
    if (!form.bloodGroup.trim()) {
      toast.error(tm('bloodGroupRequired'))
      return
    }
    if (!form.medicalCompanyId || form.medicalCompanyId === 0) {
      toast.error(tm('medicalCompanyRequired'))
      return
    }
    if (!form.medicalDetail.trim()) {
      toast.error(tm('medicalDetailRequired'))
      return
    }

    const payload: MedicalVm = {
      ...(isEditing && id ? { id: Number(id) } : {}),
      empId: form.empId,
      employeeId: form.empId,
      medicalClassId: form.medicalClassId,
      medicalCard: form.medicalCard.trim(),
      issueDate: form.issueDate || undefined,
      expiryDate: form.expiryDate || undefined,
      bloodGroup: form.bloodGroup.trim(),
      medicalCompanyId: form.medicalCompanyId,
      medicalDetail: form.medicalDetail.trim(),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empMedicalService.update(payload)
        await showSuccess(tm('updatedTitle'), tm('updatedMessage'))
      } else {
        await empMedicalService.save(payload)
        await showSuccess(tm('savedTitle'), tm('savedMessage'))
      }
      navigate('/medicals')
    } catch (e: any) {
      await showError(tm('saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? tm('saveFailedMessage'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={tm('formTitle')} description={tm('formDescriptionGeneric')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{tm('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={tm('formTitle')}
        description={isEditing ? tm('formDescriptionEdit') : tm('formDescriptionAdd')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{tm('cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>
                {tm('requiredMark')} {tm('labelEmployee')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: tm('placeholderSelectEmployee') }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder={tm('placeholderSelectEmployee')}
                searchPlaceholder={tm('searchEmployeePlaceholder')}
                emptyMessage={tm('emptyEmployee')}
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tm('requiredMark')} {tm('labelInsuranceClassType')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: tm('placeholderSelectClass') }, ...medicalClassOptions]}
                value={form.medicalClassId ? String(form.medicalClassId) : '0'}
                onValueChange={(v) => handleChange({ medicalClassId: Number(v) || 0 })}
                placeholder={tm('placeholderInsuranceClass')}
                searchPlaceholder={tm('searchClassPlaceholder')}
                emptyMessage={tm('emptyClass')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tm('requiredMark')} {tm('labelMedicalCard')}
              </Label>
              <Input
                value={form.medicalCard}
                onChange={(e) => handleChange({ medicalCard: e.target.value })}
                placeholder={tm('placeholderMedicalCard')}
              />
            </div>
            <div className="space-y-2">
              <Label>{tm('labelIssueDate')}</Label>
              <Input
                type="date"
                value={form.issueDate}
                onChange={(e) => handleChange({ issueDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{tm('labelExpiryDate')}</Label>
              <Input
                type="date"
                value={form.expiryDate}
                onChange={(e) => handleChange({ expiryDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tm('requiredMark')} {tm('labelBloodGroup')}
              </Label>
              <Input
                value={form.bloodGroup}
                onChange={(e) => handleChange({ bloodGroup: e.target.value })}
                placeholder={tm('placeholderBloodGroup')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tm('requiredMark')} {tm('labelMedicalCompany')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: tm('placeholderSelectCompany') }, ...medicalCompanyOptions]}
                value={form.medicalCompanyId ? String(form.medicalCompanyId) : '0'}
                onValueChange={(v) => handleChange({ medicalCompanyId: Number(v) || 0 })}
                placeholder={tm('placeholderMedicalCompany')}
                searchPlaceholder={tm('searchCompanyPlaceholder')}
                emptyMessage={tm('emptyCompany')}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>
              {tm('requiredMark')} {tm('labelMedicalDetail')}
            </Label>
            <Textarea
              value={form.medicalDetail}
              onChange={(e) => handleChange({ medicalDetail: e.target.value })}
              placeholder={tm('placeholderMedicalDetail')}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/medicals')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {tm('return')}
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
